import { Fragment, useEffect, useMemo, useState } from 'react';
import { Clock, Package, MapPin, Truck, CheckCircle, XCircle, FileText, Send, Calendar, AlertTriangle, TrendingDown, Share2, ArrowUp, ArrowDown, ChevronRight, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useAuditLog } from '../hooks/useAuditLog';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { bidService } from '../services/bidService';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { shareTargetService } from '../services/shareTargetService';
import { whatsappService } from '../services/whatsappService';
import { driverService } from '../services/driverService';
import { taskNotificationService } from '../services/taskNotificationService';

const viteEnv = ((import.meta as any)?.env || {}) as Record<string, string | undefined>;
const OFFER_REQUEST_SEEN_COUNTS_KEY = 'offerRequestSeenCounts';

const openGoogleMapsCoordinates = (latitude?: number, longitude?: number) => {
  if (latitude == null || longitude == null) return;
  window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank', 'noopener,noreferrer');
};

interface OfferRequest {
  id: string;
  sevkKodu: string;
  shipmentWorkiomRecordId?: string;
  shipmentStatus?: string;
  transportModel?: string;
  isExpired?: boolean;
  createdAt: string;
  validUntil: string;
  yuklemeMusterisiAd: string;
  yuklemeIl: string;
  yuklemeIlce?: string;
  teslimMusterisiAd: string;
  teslimIl: string;
  teslimIlce?: string;
  yukTuru?: string;
  aracTipi: string;
  irtibatTelefon?: string;
  loadingWarehouseName?: string;
  loadingWarehouseManagerPhone?: string;
  loadingWarehouseAccountingPhone?: string;
  yukAgirlik: number;
  mesafe: number;
  status: 'in-progress' | 'completed' | 'cancelled';
  offerCount: number;
  lowestOffer?: number;
  highestOffer?: number;
  acceptedOffer?: number;
}

interface SupplierOffer {
  id: string;
  workiomRecordId?: string;
  offerRequestId: string;
  carrierCompanyName?: string;
  supplierName: string;
  supplierContact: string;
  vehiclePlate?: string;
  trailerPlate?: string;
  vehicleType?: string;
  bidLatitude?: number;
  bidLongitude?: number;
  bidLocationTime?: string;
  distanceToLoadingKm?: number | null;
  offerAmount: number;
  validUntil: string;
  notes?: string;
  status: 'pending' | 'accepted' | 'rejected';
  submittedAt: string;
}

export function OfferRequests() {
  const { logAction } = useAuditLog();
  const { user } = useAuth();
  const [requests, setRequests] = useState<OfferRequest[]>([]);
  const [offers, setOffers] = useState<SupplierOffer[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<OfferRequest | null>(null);
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [sharePreview, setSharePreview] = useState<{ open: boolean; channel: 'whatsapp' | 'facebook'; message: string; requestCount: number; sending: boolean }>({ open: false, channel: 'whatsapp', message: '', requestCount: 0, sending: false });
  const [filter, setFilter] = useState<'all' | 'in-progress' | 'completed' | 'cancelled' | 'expired'>('in-progress');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyWithOffers, setOnlyWithOffers] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ field: keyof OfferRequest; direction: 'asc' | 'desc' } | null>({ field: 'sevkKodu', direction: 'asc' });
  const [groupBy, setGroupBy] = useState<'sevkKodu' | 'status' | 'yuklemeIl' | 'teslimIl'>('sevkKodu');
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [externalDrivers, setExternalDrivers] = useState<Array<{ id: string; applicationId: string; name: string; phone: string; plate: string; city: string; workiomRecordId: string }>>([]);
  const [manualBidDriverSearch, setManualBidDriverSearch] = useState('');
  const [manualBidModal, setManualBidModal] = useState<{ open: boolean; request: OfferRequest | null; driverId: string; driverWorkiomId: string; driverPlate: string; driverTrailerPlate: string; driverVehicleType: string; amount: string; notes: string; saving: boolean }>({
    open: false,
    request: null,
    driverId: '',
    driverWorkiomId: '',
    driverPlate: '',
    driverTrailerPlate: '',
    driverVehicleType: '',
    amount: '',
    notes: '',
    saving: false,
  });
  const [editOfferModal, setEditOfferModal] = useState<{ open: boolean; offer: SupplierOffer | null; amount: string; notes: string; saving: boolean }>({
    open: false,
    offer: null,
    amount: '',
    notes: '',
    saving: false,
  });
  const [rejectOfferModal, setRejectOfferModal] = useState<{ open: boolean; offer: SupplierOffer | null; reason: string; saving: boolean }>({ open: false, offer: null, reason: '', saving: false });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [seenOfferCounts, setSeenOfferCounts] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem(OFFER_REQUEST_SEEN_COUNTS_KEY) || '{}');
    } catch {
      return {};
    }
  });

  const appDownloadUrl = (viteEnv.VITE_DRIVER_APP_DOWNLOAD_URL || '').trim();

  const formatDisplayPhone = (value?: string) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '-';
    const normalized = digits.startsWith('90') ? digits : (digits.startsWith('0') ? `9${digits}` : digits);
    if (normalized.length === 12 && normalized.startsWith('90')) {
      return `+${normalized.slice(0, 2)} ${normalized.slice(2, 5)} ${normalized.slice(5, 8)} ${normalized.slice(8, 10)} ${normalized.slice(10, 12)}`;
    }
    return value || normalized;
  };

  const buildBulkOfferShareMessage = (selectedRequests: OfferRequest[]) => {
    const firstRequest = selectedRequests[0];
    const pickupParts = Array.from(new Set(selectedRequests.map((request) => [request.yuklemeIlce, request.yuklemeIl].filter(Boolean).join(' ')).filter(Boolean)));
    const loadTypeParts = Array.from(new Set(selectedRequests.map((request) => String(request.yukTuru || '').trim()).filter(Boolean)));
    const vehicleTypeParts = Array.from(new Set(selectedRequests.map((request) => String(request.aracTipi || '').trim()).filter(Boolean)));
    const maxWeight = Math.max(...selectedRequests.map((request) => Number(request.yukAgirlik || 0)), 0);

    const pickupGroupMap = new Map<string, OfferRequest[]>();
    selectedRequests.forEach((request) => {
      const pickupKey = [request.yuklemeMusterisiAd, request.yuklemeIl].filter(Boolean).join(' / ') || '-';
      if (!pickupGroupMap.has(pickupKey)) pickupGroupMap.set(pickupKey, []);
      pickupGroupMap.get(pickupKey)?.push(request);
    });
    const pickupPhoneEntries: Array<{ label: string; phone: string }> = [];
    pickupGroupMap.forEach((groupRequests, pickupKey) => {
      const uniquePhones = Array.from(new Set(groupRequests.map((request) => String(request.irtibatTelefon || '').trim()).filter(Boolean)));
      const phone = uniquePhones[0] || '';
      if (phone) pickupPhoneEntries.push({ label: pickupKey, phone });
    });
    const contactPhoneLines = pickupPhoneEntries.map((entry, index, all) => {
      const formatted = formatDisplayPhone(entry.phone);
      return all.length <= 1 ? `☎️ tel:${formatted}` : `☎️ tel:${formatted} (${entry.label})`;
    });

    const cityGroups = selectedRequests.reduce((acc: Record<string, OfferRequest[]>, request) => {
      const key = request.teslimIl || '-';
      if (!acc[key]) acc[key] = [];
      acc[key].push(request);
      return acc;
    }, {});

    const cityLines = Object.entries(cityGroups).flatMap(([city, requests]) => {
      const lines = [`📍 ${city}`];
      const districtSummaryMap = new Map<string, { pointNames: Set<string>; vehicleCount: number }>();
      requests.forEach((request) => {
        const rawDistricts = String(request.teslimIlce || '').split('|').map((item) => item.trim()).filter(Boolean);
        const pointNames = String(request.teslimMusterisiAd || '').split('|').map((item) => item.trim()).filter(Boolean);
        const effectiveDistricts = rawDistricts.length > 0 ? rawDistricts : ['Belirtilmemiş'];
        const key = effectiveDistricts.join(', ');
        const current = districtSummaryMap.get(key) || { pointNames: new Set<string>(), vehicleCount: 0 };
        const effectivePointNames = pointNames.length > 0 ? pointNames : [key];
        effectivePointNames.forEach((name) => current.pointNames.add(name));
        current.vehicleCount += 1;
        districtSummaryMap.set(key, current);
      });
      Array.from(districtSummaryMap.entries()).forEach(([districts, summary]) => {
        const pointCount = Math.max(1, summary.pointNames.size);
        const pointLabel = pointCount <= 1 ? 'Tek Nokta' : `${pointCount} Nokta`;
        const vehicleLabel = summary.vehicleCount <= 1 ? '1 Araç' : `${summary.vehicleCount} Araç`;
        lines.push(`- ${districts} (${pointLabel} / ${vehicleLabel})`);
      });
      return lines;
    });

    return [
      `📢 DOĞUŞ ÇAY / ${pickupParts.join(' & ') || '-'} ÇIKIŞLI${loadTypeParts.length > 0 ? ` (${loadTypeParts.join(' / ')})` : ''}`,
      '───────────────',
      ...cityLines,
      '───────────────',
      '🕒 7/24 Yükleme',
      `⚖️ ${maxWeight > 0 && maxWeight <= 5000 ? '0-5 Ton (Hafif Yük)' : `${(maxWeight / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} Ton`}`,
      `🚛 ${vehicleTypeParts.join(' / ') || '-'}`,
      '☁️ Araç temiz ve kokusuz',
      '📱 Fatura ve Evraklar "Yük Ortağım" Uygulamasına Yüklenecektir',
      '💸 MASRAF/KOMİSYON YOK',
      `Teklif linki: ${appDownloadUrl || 'https://yukortagim.doguscay.com.tr/about'}`,
      ...(contactPhoneLines.length > 0 ? contactPhoneLines : [`☎️ tel:${formatDisplayPhone(firstRequest?.irtibatTelefon)}`]),
    ].join('\n');
  };

  useEffect(() => {
    loadData();
    loadExternalDrivers();
  }, []);

  useAutoRefresh(() => loadData(), { intervalMs: 30000 });

  async function loadExternalDrivers() {
    try {
      const response: any = await driverService.getApplications({ status: 'approved' });
      const data = Array.isArray(response?.data) ? response.data : [];
      setExternalDrivers(data.map((item: any) => ({
        id: String(item.driver_id || item.id),
        applicationId: String(item.id || item.application_id || ''),
        name: item.full_name || item.name || item.driver_name || '-',
        phone: item.phone || '',
        plate: item.cekici_plaka || item.vehicle_plate || '',
        city: item.city || '',
        workiomRecordId: item.workiom_record_id || '',
      })));
    } catch (driverError) {
      console.error('External drivers load failed:', driverError);
    }
  }

  const filteredExternalDrivers = useMemo(() => {
    const query = manualBidDriverSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return externalDrivers;
    return externalDrivers.filter((driver) => (
      [driver.name, driver.phone, driver.plate, driver.city].join(' ').toLocaleLowerCase('tr-TR').includes(query)
    ));
  }, [externalDrivers, manualBidDriverSearch]);

  function getRequestOffers(requestId: string) {
    return offers.filter(o => o.offerRequestId === requestId).sort((a, b) => a.offerAmount - b.offerAmount);
  }

  function getRequestCode(requestId: string, fallback = '') {
    return requests.find((item) => item.id === requestId)?.sevkKodu || fallback;
  }

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [requestsResponse, bidsResponse] = await Promise.all([
        bidService.getBidRequests(),
        bidService.getAll()
      ]);

      const requestItems = Array.isArray((requestsResponse as any)?.data) ? (requestsResponse as any).data : [];
      const mappedRequests: OfferRequest[] = requestItems.map((item: any) => {
        let status: OfferRequest['status'] = 'in-progress';
        const isExpired = new Date(item.bid_deadline || item.valid_until || item.delivery_date || new Date().toISOString()) < new Date();
        if (item.shipment_status === 'assigned' && (item.transport_model === 'own-fleet' || item.transport_model === 'filo')) {
          status = 'completed';
        } else if (item.shipment_status === 'assigned' && (item.transport_model === 'spot' || item.transport_model === 'bid')) {
          status = 'completed';
        } else if (item.status === 'awarded') {
          status = 'completed';
        } else if (item.status === 'cancelled') {
          status = 'cancelled';
        } else if (item.status === 'closed') {
          status = 'completed';
        }

        return {
          id: String(item.id),
          sevkKodu: item.shipment_no || item.sevk_kodu || `#${item.id}`,
          shipmentWorkiomRecordId: item.shipment_workiom_record_id ? String(item.shipment_workiom_record_id) : (item.workiom_record_id ? String(item.workiom_record_id) : undefined),
          shipmentStatus: item.shipment_status,
          transportModel: item.transport_model,
          isExpired,
          createdAt: item.created_at || item.pickup_date || new Date().toISOString(),
          validUntil: item.bid_deadline || item.valid_until || item.delivery_date || new Date().toISOString(),
          yuklemeMusterisiAd: item.loading_point || item.loading_address || '-',
          yuklemeIl: item.loading_city || '-',
          yuklemeIlce: item.loading_district || item.pickup_district || '',
          teslimMusterisiAd: item.delivery_points_text || item.delivery_point || item.delivery_address || '-',
          teslimIl: item.delivery_city || '-',
          teslimIlce: item.delivery_district || '',
          yukTuru: item.load_type || '',
          aracTipi: item.vehicle_type || item.load_type || '-',
          loadingWarehouseName: item.loading_warehouse_name || '',
          loadingWarehouseManagerPhone: item.loading_warehouse_manager_phone || '',
          loadingWarehouseAccountingPhone: item.loading_warehouse_accounting_phone || '',
          irtibatTelefon: item.loading_warehouse_accounting_phone || item.loading_warehouse_manager_phone || item.loading_contact_phone_resolved || item.loading_contact_phone || item.shipment_contact_phone_resolved || item.shipment_contact_phone || '',
          yukAgirlik: Number(item.cargo_weight || item.weight_kg || 0),
          mesafe: Number(item.distance_km || 0),
          status,
          offerCount: Number(item.total_bids || item.bid_count || 0),
          lowestOffer: item.lowest_bid ? Number(item.lowest_bid) : undefined,
          highestOffer: item.max_bid ? Number(item.max_bid) : undefined,
          acceptedOffer: item.status === 'awarded' ? Number(item.current_lowest_bid || item.lowest_bid || 0) : undefined,
        };
      });

      const bidItems = Array.isArray((bidsResponse as any)?.data) ? (bidsResponse as any).data : [];
      const mappedOffers: SupplierOffer[] = bidItems.map((item: any) => {
        let status: SupplierOffer['status'] = 'pending';
        if (item.status === 'accepted') {
          status = 'accepted';
        } else if (item.status === 'rejected') {
          status = 'rejected';
        }

        return {
          id: String(item.id),
          workiomRecordId: item.workiom_record_id ? String(item.workiom_record_id) : undefined,
          offerRequestId: String(item.bid_request_id || item.offerRequestId || ''),
          carrierCompanyName: item.carrier_company_name || '',
          supplierName: item.user_driver_name || item.driver_name || item.supplierName || 'Tedarikçi',
          supplierContact: item.driver_phone || item.supplierContact || '',
          vehiclePlate: item.tractor_plate || item.cekici_plaka || '',
          trailerPlate: item.trailer_plate || item.dorse_plaka || '',
          vehicleType: item.vehicle_type || '',
          bidLatitude: item.bid_latitude ? Number(item.bid_latitude) : undefined,
          bidLongitude: item.bid_longitude ? Number(item.bid_longitude) : undefined,
          bidLocationTime: item.bid_location_time,
          distanceToLoadingKm: item.bid_distance_to_loading_km,
          offerAmount: Number(item.bid_amount || item.bidAmount || 0),
          validUntil: item.valid_until || item.bid_deadline || item.created_at || new Date().toISOString(),
          notes: item.notes,
          status,
          submittedAt: item.created_at || item.submittedAt || new Date().toISOString(),
        };
      });

      setRequests(mappedRequests);
      setOffers(mappedOffers);
    } catch (err: any) {
      setError(err.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }

  function saveOffers(data: SupplierOffer[]) {
    setOffers(data);
  }

  function recomputeRequestSummaries(nextOffers: SupplierOffer[]) {
    setRequests((current) => current.map((request) => {
      const requestOffers = nextOffers.filter((offer) => offer.offerRequestId === request.id && offer.status === 'pending').sort((a, b) => a.offerAmount - b.offerAmount);
      return {
        ...request,
        offerCount: requestOffers.length,
        lowestOffer: requestOffers[0]?.offerAmount,
      };
    }));
  }

  async function createNotification(type: string, title: string, message: string, relatedId?: string) {
    if (!user?.id) return;

    try {
      await notificationService.create({
        userId: user.id,
        type,
        title,
        message,
        relatedId,
      });
      window.dispatchEvent(new Event('notificationUpdate'));
    } catch (error) {
      console.error('Notification creation failed:', error);
    }
  }

  async function handleAcceptOffer(offerId: string) {
    if (!window.confirm('Bu teklifi kabul etmek istediğinizden emin misiniz?')) return;

    const offer = offers.find(o => o.id === offerId);
    if (!offer) return;

    const request = requests.find(r => r.id === offer.offerRequestId);
    const requestCode = request?.sevkKodu || '';

    try {
      await bidService.accept(offerId);
      await bidService.closeBidRequest(offer.offerRequestId).catch((error) => console.error('Close bid request after accept failed:', error));
      
      const updatedOffers = offers.map(o => {
        if (o.id === offerId) {
          return { ...o, status: 'accepted' as const };
        }
        if (o.offerRequestId === offer.offerRequestId && o.id !== offerId) {
          return { ...o, status: 'rejected' as const };
        }
        return o;
      });
      saveOffers(updatedOffers);

        const updatedRequests = requests.map(r => {
          if (r.id === offer.offerRequestId) {
            return {
              ...r,
              status: 'completed' as const,
              acceptedOffer: offer.offerAmount,
            };
          }
        return r;
      });
      setRequests(updatedRequests);

      logAction.custom({
        category: 'BID',
        action: 'APPROVE',
        entity: 'OfferRequest',
        entityId: offer.offerRequestId,
        entityName: requestCode,
        description: `Teklif kabul edildi: ${offer.supplierName} - ₺${offer.offerAmount.toLocaleString('tr-TR')}`,
        metadata: {
          supplierName: offer.supplierName,
          offerAmount: offer.offerAmount,
          sevkKodu: requestCode || undefined,
        },
        severity: 'info',
      });

      createNotification(
        'offer-accepted',
        `Teklif Kabul Edildi`,
        `#${requestCode || offer.offerRequestId} sevkiyatı için ${offer.supplierName} teklifini kabul ettiniz (${offer.offerAmount.toLocaleString('tr-TR')} ₺)`
      );

      await taskNotificationService.notifyAssignment({
        phoneNumber: offer.supplierContact,
        shipmentNo: requestCode || offer.offerRequestId,
        loadingCustomer: request?.yuklemeMusterisiAd,
        loadingCity: request?.yuklemeIl,
        deliveryCustomer: request?.teslimMusterisiAd,
        deliveryCity: request?.teslimIl,
        vehicleType: offer.vehicleType || request?.aracTipi,
        weightKg: request?.yukAgirlik,
        distanceKm: request?.mesafe,
        agreedPrice: offer.offerAmount,
      }).catch((error) => console.error('Offer accept notification failed:', error));
    } catch (err) {
      alert('Teklif kabul edilirken bir hata oluştu');
    }
  }

  function handleRejectOffer(offerId: string) {
    const offer = offers.find(o => o.id === offerId);
    if (!offer) return;

    setRejectOfferModal({ open: true, offer, reason: '', saving: false });
  }

  async function submitRejectOffer() {
    if (!rejectOfferModal.offer) return;

    const offer = rejectOfferModal.offer;
    const request = requests.find(r => r.id === offer.offerRequestId);
    const requestCode = request?.sevkKodu || '';

    try {
      setRejectOfferModal((current) => ({ ...current, saving: true }));
      await bidService.reject(offer.id, { reason: rejectOfferModal.reason });

      const updatedOffers = offers.map((o) => {
        if (o.id !== offer.id) return o;
        return {
          ...o,
          status: 'rejected' as const,
          notes: rejectOfferModal.reason
            ? `${o.notes ? `${o.notes}\n` : ''}Ret Sebebi: ${rejectOfferModal.reason}`
            : o.notes,
        };
      });
      saveOffers(updatedOffers);
      recomputeRequestSummaries(updatedOffers);
      setRejectOfferModal({ open: false, offer: null, reason: '', saving: false });

      logAction.custom({
        category: 'BID',
        action: 'REJECT',
        entity: 'OfferRequest',
        entityId: offer.offerRequestId,
        entityName: requestCode,
        description: `Teklif reddedildi: ${offer.supplierName} - ₺${offer.offerAmount.toLocaleString('tr-TR')}`,
        metadata: {
          supplierName: offer.supplierName,
          offerAmount: offer.offerAmount,
          sevkKodu: requestCode || undefined,
          reason: rejectOfferModal.reason,
        },
        severity: 'warning',
      });
    } catch (err) {
      alert('Teklif reddedilirken bir hata oluştu');
      setRejectOfferModal((current) => ({ ...current, saving: false }));
    }
  }

  async function handleRemoveFromWaiting(requestId: string) {
    if (!window.confirm('Bu kaydı teklif bekleyen süreçten çıkarmak istediğinizden emin misiniz?')) return;

    const requestCode = getRequestCode(requestId, requestId);

    try {
      await bidService.cancelBidRequest(requestId);
      
      logAction.custom({
        category: 'BID',
        action: 'CANCEL',
        entity: 'OfferRequest',
        entityId: requestId,
        entityName: requestCode,
        description: `${requestCode} teklif sürecinden çıkarıldı`,
        severity: 'warning',
      });

      createNotification('offer-cancelled', 'Teklif Süreci Sonlandırıldı', `#${requestCode} kaydı teklif bekleyen süreçten çıkarıldı.`);
      
      await loadData();
    } catch (error) {
      console.error('Remove from waiting failed:', error);
      alert('Kayıt teklif sürecinden çıkarılırken hata oluştu');
    }
  }

  async function handleDeleteBidRequest(requestId: string) {
    if (!window.confirm('Bu teklif talebini tamamen silmek istediğinizden emin misiniz?')) return;

    const requestCode = getRequestCode(requestId, requestId);

    try {
      await bidService.deleteBidRequest(requestId);

      logAction.custom({
        category: 'BID',
        action: 'DELETE',
        entity: 'OfferRequest',
        entityId: requestId,
        entityName: requestCode,
        description: `${requestCode} teklif talebi silindi`,
        severity: 'warning',
      });

      createNotification('offer-deleted', 'Teklif Talebi Silindi', `#${requestCode} teklif talebi silindi.`);
      await loadData();
    } catch (error) {
      console.error('Delete bid request failed:', error);
      alert('Teklif talebi silinirken hata oluştu');
    }
  }

  const filteredRequests = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase('tr-TR');
    const activeColumnFilters = Object.entries(columnFilters).filter(([, value]) => !!value);

    return requests.filter((request) => {
      if (filter === 'expired' && !(new Date(request.validUntil) < new Date() && request.status !== 'completed' && request.status !== 'cancelled')) return false;
      if (filter === 'in-progress' && !(request.status === 'in-progress' && new Date(request.validUntil) >= new Date())) return false;
      if (filter !== 'all' && filter !== 'expired' && filter !== 'in-progress' && request.status !== filter) return false;

      const relatedOffers = offers.filter((offer) => offer.offerRequestId === request.id);
      if (onlyWithOffers && relatedOffers.length === 0) return false;

      if (query) {
        const offerSearchText = relatedOffers.map((offer) => [offer.supplierName, offer.vehiclePlate, offer.trailerPlate, offer.supplierContact].join(' ')).join(' ');
        const requestSearchText = [request.sevkKodu, request.yuklemeMusterisiAd, request.yuklemeIl, request.teslimMusterisiAd, request.teslimIl, offerSearchText].join(' ').toLocaleLowerCase('tr-TR');
        if (!requestSearchText.includes(query)) return false;
      }

      return activeColumnFilters.every(([key, value]) => String((request as any)[key] || '').toLocaleLowerCase('tr-TR').includes(value.toLocaleLowerCase('tr-TR')));
    });
  }, [requests, offers, filter, searchTerm, onlyWithOffers, columnFilters]);

  const sortedRequests = useMemo(() => {
    if (!sortConfig) return filteredRequests;
    return [...filteredRequests].sort((a, b) => {
      const left = String(a[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      const right = String(b[sortConfig.field] ?? '').toLocaleLowerCase('tr-TR');
      if (left < right) return sortConfig.direction === 'asc' ? -1 : 1;
      if (left > right) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRequests, sortConfig]);

  const groupedRequests = useMemo(() => {
    return sortedRequests.reduce((acc: Record<string, OfferRequest[]>, item) => {
      const key = String(item[groupBy] || '-');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [sortedRequests, groupBy]);

  const groupedByPickupLocation = useMemo(() => {
    return sortedRequests.reduce((acc: Record<string, OfferRequest[]>, item) => {
      const key = [item.yuklemeMusterisiAd || '-', item.yuklemeIl || '-'].filter(Boolean).join(' / ');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [sortedRequests]);

  useEffect(() => {
    setSelectedRequest(null);
    setSelectedRequestIds([]);
  }, [filter, searchTerm, onlyWithOffers, columnFilters, requests.length]);

  useEffect(() => {
    setExpandedGroups({});
  }, [filter, searchTerm, onlyWithOffers, columnFilters, requests.length]);

  useEffect(() => {
    localStorage.setItem(OFFER_REQUEST_SEEN_COUNTS_KEY, JSON.stringify(seenOfferCounts));
  }, [seenOfferCounts]);

  function handleSort(field: keyof OfferRequest) {
    setSortConfig((current) => {
      if (!current || current.field !== field) return { field, direction: 'asc' };
      if (current.direction === 'asc') return { field, direction: 'desc' };
      return null;
    });
  }

  const renderSortableHeader = (label: string, field: keyof OfferRequest) => (
    <button type="button" onClick={() => handleSort(field)} className="inline-flex items-center gap-1 font-semibold text-gray-700">
      <span>{label}</span>
      {sortConfig?.field === field ? (
        sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-600" /> : <ArrowDown className="w-3 h-3 text-blue-600" />
      ) : (
        <ArrowUp className="w-3 h-3 text-gray-300" />
      )}
    </button>
  );

  function getGroupLabel(field: typeof groupBy) {
    switch (field) {
      case 'sevkKodu':
        return 'Sevk Kodu';
      case 'status':
        return 'Durum';
      case 'yuklemeIl':
        return 'Yükleme İli';
      case 'teslimIl':
        return 'Teslim İli';
      default:
        return 'Grup';
    }
  }

  function getRequestStatusBadge(request: OfferRequest) {
    if (request.status === 'cancelled') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center gap-1"><XCircle className="w-3 h-3" />İptal</span>;
    }
    if (request.shipmentStatus === 'assigned' && (request.transportModel === 'own-fleet' || request.transportModel === 'filo')) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-sky-100 text-sky-800 flex items-center gap-1"><Truck className="w-3 h-3" />Filo Tedarik Edildi</span>;
    }
    if (request.shipmentStatus === 'assigned' && (request.transportModel === 'spot' || request.transportModel === 'bid')) {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Spot Tedarik Edildi</span>;
    }
    if (request.isExpired && request.status !== 'completed') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Teklif Süresi Geçti</span>;
    }
    switch (request.status) {
      case 'in-progress':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1"><Clock className="w-3 h-3" />Teklif Aşamasında</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Teklif Sonuçlandı</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center gap-1"><XCircle className="w-3 h-3" />İptal</span>;
    }
  }

  const stats = {
    total: requests.length,
    inProgress: requests.filter(r => r.status === 'in-progress' && new Date(r.validUntil) >= new Date()).length,
    completed: requests.filter(r => r.status === 'completed').length,
    cancelled: requests.filter(r => r.status === 'cancelled').length,
    expired: requests.filter(r => new Date(r.validUntil) < new Date() && r.status !== 'completed' && r.status !== 'cancelled').length,
  };

  function buildShareMessage(request: OfferRequest) {
    return buildBulkOfferShareMessage([request]);
  }

  async function handleShareRequest(request: OfferRequest) {
    let shareText = buildShareMessage(request);

    try {
      const queueResponse = await shareTargetService.queueBidRequestShares(request.id, shareText);
        const targets = Array.isArray((queueResponse as any)?.data) ? (queueResponse as any).data : [];

      if (targets.length === 0) {
        if (navigator.share) {
          await navigator.share({
            title: `Teklif Talebi - ${request.sevkKodu}`,
            text: shareText,
            url: appDownloadUrl,
          });
          return;
        }

        await navigator.clipboard.writeText(shareText);
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
        alert('Paylaşım hedefi tanımlı değil. Metin panoya kopyalandı ve genel WhatsApp paylaşımı açıldı.');
        return;
      }

      await navigator.clipboard.writeText(shareText);
      let openedCount = 0;

      targets.forEach((target: any) => {
        if (target.platform === 'whatsapp' && target.phone_number) {
          openedCount += 1;
          window.open(`https://wa.me/${String(target.phone_number).replace(/\D/g, '')}?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
        }
        if (target.platform === 'facebook' && target.target_url) {
          openedCount += 1;
          window.open(target.target_url, '_blank', 'noopener,noreferrer');
        }
      });

      alert(`${targets.length} paylaşım hedefi kuyruğa alındı. ${openedCount} hedef için pencere açıldı. Paylaşım metni panoya kopyalandı.`);
    } catch (shareError) {
      console.error('Share request failed:', shareError);
      try {
        await navigator.clipboard.writeText(shareText);
        alert('Paylaşım metni panoya kopyalandı. WhatsApp veya Facebook hedeflerine manuel paylaşabilirsiniz.');
      } catch (clipboardError) {
        console.error('Clipboard fallback failed:', clipboardError);
        alert('Paylaşım başarısız oldu.');
      }
    }
  }

  function openBulkSharePreview(channel: 'whatsapp' | 'facebook') {
    const selectedRequests = filteredRequests.filter((request) => selectedRequestIds.includes(request.id));
    if (selectedRequests.length === 0) {
      alert('Lütfen en az bir teklif talebi seçin');
      return;
    }

    setSharePreview({
      open: true,
      channel,
      message: buildBulkOfferShareMessage(selectedRequests),
      requestCount: selectedRequests.length,
      sending: false,
    });
  }

  async function submitBulkWhatsappShare() {
    const selectedRequests = filteredRequests.filter((request) => selectedRequestIds.includes(request.id));
    if (selectedRequests.length === 0) {
      alert('Lütfen en az bir teklif talebi seçin');
      return;
    }

    try {
      setSharePreview((current) => ({ ...current, sending: true }));
      const whatsappMessage = sharePreview.message.trim();
      if (!whatsappMessage) throw new Error('Gönderilecek mesaj boş olamaz');
      const targetsResponse: any = await shareTargetService.getTargets();
      const targets = Array.isArray(targetsResponse?.data) ? targetsResponse.data : [];
      const whatsappTargets = targets.filter((target: any) => target.platform === 'whatsapp' && target.phone_number);
      if (whatsappTargets.length === 0) {
        throw new Error('Aktif WhatsApp paylaşım hedefi bulunamadı');
      }

      await Promise.all(
        whatsappTargets.map((target: any) => whatsappService.send({
          phoneNumber: String(target.phone_number),
          message: whatsappMessage,
        }))
      );

      try {
        await navigator.clipboard.writeText(whatsappMessage);
      } catch (clipboardError) {
        console.warn('Clipboard copy failed:', clipboardError);
      }

      setSharePreview({ open: false, channel: 'whatsapp', message: '', requestCount: 0, sending: false });
      alert(`${selectedRequests.length} teklif talebi için mesaj ${whatsappTargets.length} WhatsApp hedefine API üzerinden gönderildi.`);
    } catch (error) {
      console.error('Bulk WhatsApp share failed:', error);
      alert('Toplu WhatsApp API gönderimi başarısız oldu. Paylaşım hedeflerini ve API ayarlarını kontrol edin.');
    } finally {
      setSharePreview((current) => ({ ...current, sending: false }));
    }
  }

  async function openBulkWhatsappLocally() {
    if (!sharePreview.message.trim()) {
      alert('Gönderilecek mesaj boş olamaz');
      return;
    }

    try {
      const popup = window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(sharePreview.message)}`, '_blank', 'noopener,noreferrer');
      if (!popup) throw new Error('WhatsApp penceresi engellendi');
      try {
        await navigator.clipboard.writeText(sharePreview.message);
      } catch (clipboardError) {
        console.warn('Clipboard copy failed:', clipboardError);
      }
      alert('Mesaj WhatsApp uygulamasında / WhatsApp Web’de açıldı ve panoya kopyalandı.');
    } catch (error) {
      console.error('Local WhatsApp open failed:', error);
      alert('WhatsApp açılamadı. Tarayıcı popup engelini kontrol edin.');
    }
  }

  async function openBulkFacebookLocally() {
    if (!sharePreview.message.trim()) {
      alert('Gönderilecek mesaj boş olamaz');
      return;
    }

    try {
      await navigator.clipboard.writeText(sharePreview.message);
      const targetsResponse: any = await shareTargetService.getTargets();
      const targets = Array.isArray(targetsResponse?.data) ? targetsResponse.data : [];
      const facebookTargets = targets.filter((target: any) => target.platform === 'facebook' && target.target_url);
      let opened = 0;
      if (facebookTargets.length > 0) {
        facebookTargets.forEach((target: any) => {
          window.open(String(target.target_url), '_blank', 'noopener,noreferrer');
          opened += 1;
        });
      } else {
        const popup = window.open('https://www.facebook.com/', '_blank', 'noopener,noreferrer');
        if (!popup) throw new Error('Facebook penceresi engellendi');
        opened = 1;
      }
      alert(`Facebook açıldı. Mesaj panoya kopyalandı; ${opened} hedefte yapıştırıp paylaşabilirsiniz.`);
    } catch (error) {
      console.error('Local Facebook open failed:', error);
      alert('Facebook açılamadı veya pano kopyalama başarısız oldu.');
    }
  }

  function toggleRequestSelection(requestId: string) {
    setSelectedRequestIds((current) => current.includes(requestId)
      ? current.filter((id) => id !== requestId)
      : [...current, requestId]);
  }

  function toggleGroupSelection(items: OfferRequest[]) {
    const ids = items.map((item) => item.id);
    setSelectedRequestIds((current) => {
      const allSelected = ids.every((id) => current.includes(id));
      if (allSelected) {
        return current.filter((id) => !ids.includes(id));
      }
      return ids;
    });
  }

  function openManualBidModal(request: OfferRequest) {
    setManualBidDriverSearch('');
    setManualBidModal({ open: true, request, driverId: '', driverWorkiomId: '', driverPlate: '', driverTrailerPlate: '', driverVehicleType: '', amount: '', notes: '', saving: false });
  }

  async function submitManualBid() {
    if (!manualBidModal.request) return;
    if (!manualBidModal.driverId || !manualBidModal.amount) {
      alert('Lütfen dış sürücü ve teklif tutarı seçin');
      return;
    }

    try {
      setManualBidModal((current) => ({ ...current, saving: true }));
      await bidService.create({
        shipmentId: manualBidModal.request.id,
        applicationId: manualBidModal.driverId,
        bidAmount: Number(manualBidModal.amount),
        notes: manualBidModal.notes,
        driverWorkiomId: manualBidModal.driverWorkiomId || undefined,
        tractorPlate: manualBidModal.driverPlate || undefined,
        trailerPlate: manualBidModal.driverTrailerPlate || undefined,
        vehicleType: manualBidModal.driverVehicleType || undefined,
      });
      await loadData();
      setManualBidModal({ open: false, request: null, driverId: '', driverWorkiomId: '', driverPlate: '', driverTrailerPlate: '', driverVehicleType: '', amount: '', notes: '', saving: false });
      alert('Manuel teklif başarıyla eklendi');
    } catch (manualBidError: any) {
      console.error('Manual bid create failed:', manualBidError);
      alert(manualBidError?.message || 'Manuel teklif eklenemedi');
      setManualBidModal((current) => ({ ...current, saving: false }));
    }
  }

  function openEditOfferModal(offer: SupplierOffer) {
    setEditOfferModal({
      open: true,
      offer,
      amount: String(offer.offerAmount || ''),
      notes: offer.notes || '',
      saving: false,
    });
  }

  async function submitOfferEdit() {
    if (!editOfferModal.offer || !editOfferModal.amount) {
      alert('Lütfen teklif tutarını girin');
      return;
    }

    try {
      setEditOfferModal((current) => ({ ...current, saving: true }));
      await bidService.update(editOfferModal.offer.id, {
        bidAmount: Number(editOfferModal.amount),
        notes: editOfferModal.notes,
        driverWorkiomId: editOfferModal.offer.workiomRecordId || undefined,
        tractorPlate: editOfferModal.offer.vehiclePlate || undefined,
        trailerPlate: editOfferModal.offer.trailerPlate || undefined,
        vehicleType: editOfferModal.offer.vehicleType || undefined,
      });
      await loadData();
      setEditOfferModal({ open: false, offer: null, amount: '', notes: '', saving: false });
      alert('Teklif başarıyla güncellendi');
    } catch (editError: any) {
      console.error('Offer edit failed:', editError);
      alert(editError?.message || 'Teklif güncellenemedi');
      setEditOfferModal((current) => ({ ...current, saving: false }));
    }
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Teklif Taleplerim</h1>
            <p className="text-sm text-gray-600 mt-1">Sevkiyatlar için gelen teklifleri inceleyin ve yönetin</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Toplam Talep</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
               <p className="text-sm text-gray-600">Teklif Aşamasında</p>
               <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
             </div>
           </div>
         </div>

         <div className="bg-white rounded-lg border border-gray-200 p-4">
           <div className="flex items-center gap-3">
             <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
               <CheckCircle className="w-6 h-6 text-green-600" />
             </div>
             <div>
               <p className="text-sm text-gray-600">Teklif Sonuçlandı</p>
               <p className="text-2xl font-bold text-gray-900">{stats.completed}</p>
             </div>
           </div>
         </div>

         <div className="bg-white rounded-lg border border-gray-200 p-4">
           <div className="flex items-center gap-3">
             <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
               <XCircle className="w-6 h-6 text-red-600" />
             </div>
             <div>
               <p className="text-sm text-gray-600">İptal Edilenler</p>
               <p className="text-2xl font-bold text-gray-900">{stats.cancelled}</p>
             </div>
           </div>
         </div>

         <div className="bg-white rounded-lg border border-gray-200 p-4">
           <div className="flex items-center gap-3">
             <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
               <AlertTriangle className="w-6 h-6 text-orange-600" />
             </div>
             <div>
               <p className="text-sm text-gray-600">Süresi Geçenler</p>
               <p className="text-2xl font-bold text-gray-900">{stats.expired}</p>
             </div>
           </div>
         </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Sevk kodu, yükleme, teslim ara..."
          className="min-w-[280px] rounded-lg border border-gray-300 px-4 py-2 text-sm"
        />
        <button
          onClick={() => setOnlyWithOffers((prev) => !prev)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${onlyWithOffers ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
        >
          Sadece Teklif Gelenler
        </button>
        <button
          onClick={() => setShowColumnFilters((prev) => !prev)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showColumnFilters ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
        >
          Filtrele
        </button>
        {selectedRequestIds.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => openBulkSharePreview('whatsapp')}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            >
              Toplu WhatsApp Paylaş ({selectedRequestIds.length})
            </button>
            <button
              type="button"
              onClick={() => openBulkSharePreview('facebook')}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              Toplu Facebook Paylaş ({selectedRequestIds.length})
            </button>
          </>
        )}
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Tümü ({stats.total})
        </button>
        <button
          onClick={() => setFilter('in-progress')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'in-progress'
              ? 'bg-yellow-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Teklif Aşamasında ({stats.inProgress})
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'completed'
              ? 'bg-green-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Teklif Sonuçlandı ({stats.completed})
        </button>
        <button
          onClick={() => setFilter('cancelled')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'cancelled'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          İptal Edilenler ({stats.cancelled})
        </button>
        <button
          onClick={() => setFilter('expired')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'expired'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Süresi Geçenler ({stats.expired})
        </button>
      </div>

      {showColumnFilters && (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Sevk Kodu</label>
              <input
                value={columnFilters.sevkKodu || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, sevkKodu: e.target.value }))}
                placeholder="Sevk kodu filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Yükleme</label>
              <input
                value={columnFilters.yuklemeMusterisiAd || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, yuklemeMusterisiAd: e.target.value }))}
                placeholder="Yükleme noktası filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Teslim</label>
              <input
                value={columnFilters.teslimMusterisiAd || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, teslimMusterisiAd: e.target.value }))}
                placeholder="Teslim noktası filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Araç Tipi</label>
              <input
                value={columnFilters.aracTipi || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, aracTipi: e.target.value }))}
                placeholder="Araç tipi filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Durum</label>
              <input
                value={columnFilters.status || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, status: e.target.value }))}
                placeholder="Durum filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Yükleme İli</label>
              <input
                value={columnFilters.yuklemeIl || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, yuklemeIl: e.target.value }))}
                placeholder="Yükleme ili filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Teslim İli</label>
              <input
                value={columnFilters.teslimIl || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, teslimIl: e.target.value }))}
                placeholder="Teslim ili filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Mesafe</label>
              <input
                value={columnFilters.mesafe || ''}
                onChange={(e) => setColumnFilters((prev) => ({ ...prev, mesafe: e.target.value }))}
                placeholder="Mesafe filtrele"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setColumnFilters({})}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      )}

      {/* Loading/Error States */}
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Veriler yükleniyor...</p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      )}

      {/* Request Cards */}
      {!loading && !error && (
        <div className="flex-1 overflow-y-auto">
          <div className="grid gap-5 xl:grid-cols-3 lg:grid-cols-2">
            {Object.entries(groupedByPickupLocation).map(([groupKey, items]) => (
              <div key={groupKey} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedGroups((current) => ({ ...current, [groupKey]: !current[groupKey] }))}
                  className="w-full border-b border-slate-200 bg-slate-50 px-4 py-3 text-left hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={items.length > 0 && items.every((item) => selectedRequestIds.includes(item.id))}
                        onChange={() => toggleGroupSelection(items)}
                        onClick={(event) => event.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Yükleme Noktası</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">{groupKey}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">{items.length} talep</div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expandedGroups[groupKey] ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                </button>

                {expandedGroups[groupKey] && (
                <div className="p-4 space-y-4">
                  {items.map((request) => {
                    const requestOffers = getRequestOffers(request.id);
                    const lowestOffer = requestOffers.length > 0 ? requestOffers[0] : null;
                    const expanded = selectedRequest?.id === request.id;
                    const checked = selectedRequestIds.includes(request.id);
                    const unreadOfferCount = Math.max(0, requestOffers.length - Number(seenOfferCounts[request.id] || 0));
                    return (
                      <div key={request.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => {
                            if (expanded) {
                              setSelectedRequest(null);
                              return;
                            }
                            setSelectedRequest(request);
                            setSeenOfferCounts((current) => ({ ...current, [request.id]: requestOffers.length }));
                          }}
                          className="w-full text-left hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleRequestSelection(request.id)}
                                onClick={(event) => event.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600"
                              />
                              <div className="bg-blue-100 text-blue-900 font-bold px-3 py-1 rounded-lg text-sm">
                                {request.sevkKodu}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Calendar className="w-4 h-4" />
                                {new Date(request.validUntil).toLocaleDateString('tr-TR')}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getRequestStatusBadge(request)}
                              {unreadOfferCount > 0 && (
                                <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-rose-500 px-2 py-1 text-[11px] font-semibold text-white">
                                  {unreadOfferCount}
                                </span>
                              )}
                              <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                            </div>
                          </div>

                          {lowestOffer && (
                            <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100">
                              <p className="text-sm font-bold text-emerald-800">● En düşük teklif: {lowestOffer.offerAmount.toLocaleString('tr-TR')} TL</p>
                            </div>
                          )}

                          <div className="px-4 py-4 flex items-center gap-4">
                            <div className="flex-1">
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-gray-900">{request.yuklemeMusterisiAd}</p>
                                  <p className="text-xs text-gray-600">({[request.yuklemeIl, request.yuklemeIlce].filter(Boolean).join(' / ') || '-'})</p>
                                </div>
                              </div>
                            </div>
                            <div className="text-gray-400">→</div>
                            <div className="flex-1">
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-bold text-gray-900">{request.teslimMusterisiAd}</p>
                                  <p className="text-xs text-gray-600">({[request.teslimIl, request.teslimIlce].filter(Boolean).join(' / ') || '-'})</p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-3 px-4 pb-4 text-sm">
                            <div className="rounded-lg bg-slate-50 px-3 py-2">
                              <div className="text-xs text-slate-500">Araç Tipi</div>
                              <div className="mt-1 font-semibold text-slate-800">{request.aracTipi}</div>
                            </div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2">
                              <div className="text-xs text-slate-500">Yük</div>
                              <div className="mt-1 font-semibold text-slate-800">{Number(request.yukAgirlik || 0).toLocaleString('tr-TR')} kg</div>
                            </div>
                            <div className="rounded-lg bg-slate-50 px-3 py-2">
                              <div className="text-xs text-slate-500">Mesafe</div>
                              <div className="mt-1 font-semibold text-slate-800">{request.mesafe} km</div>
                            </div>
                          </div>
                        </button>

                        <div className="border-t border-slate-100 px-4 py-3 bg-white">
                          <div className="flex flex-wrap gap-2">
                            {request.status === 'in-progress' && new Date(request.validUntil) >= new Date() && (
                              <button type="button" onClick={() => openManualBidModal(request)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700"><Send className="w-3.5 h-3.5" /> Manuel Teklif</button>
                            )}
                            <button type="button" onClick={() => handleShareRequest(request)} className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700"><Share2 className="w-3.5 h-3.5" /> Paylaş</button>
                            {request.status === 'in-progress' && new Date(request.validUntil) >= new Date() && (
                              <button type="button" onClick={() => handleRemoveFromWaiting(request.id)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700"><XCircle className="w-3.5 h-3.5" /> Süreçten Çıkar</button>
                            )}
                            {request.status === 'completed' && (
                              <button type="button" onClick={() => handleDeleteBidRequest(request.id)} className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700"><Trash2 className="w-3.5 h-3.5" /> Teklif Talebini Sil</button>
                            )}
                            {requestOffers.length > 0 && (
                              <button type="button" onClick={() => setSelectedRequest(expanded ? null : request)} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700">{expanded ? 'Teklifleri Gizle' : `Teklifleri Gör (${requestOffers.length})`}</button>
                            )}
                          </div>
                        </div>

                        {expanded && requestOffers.length > 0 && (
                          <div className="space-y-3 border-t border-slate-100 bg-slate-50 p-4">
                            {requestOffers.map((offer, index) => (
                              <div
                                key={offer.id}
                                className={`p-4 rounded-lg border ${offer.status === 'accepted' ? 'border-green-500 bg-green-50' : offer.status === 'rejected' ? 'border-gray-300 bg-gray-50 opacity-60' : index === 0 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-white'}`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <h4 className="font-bold text-gray-900">{offer.supplierName}</h4>
                                      {offer.carrierCompanyName ? <div className="mt-0.5 text-xs font-medium text-indigo-600">{offer.carrierCompanyName}</div> : null}
                                      {index === 0 && offer.status === 'pending' && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">En Düşük Teklif</span>}
                                      {offer.status === 'accepted' && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-600 text-white flex items-center gap-1"><CheckCircle className="w-3 h-3" />Kabul Edildi</span>}
                                      {offer.status === 'rejected' && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-400 text-white flex items-center gap-1"><XCircle className="w-3 h-3" />Reddedildi</span>}
                                      {offer.workiomRecordId && <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1"><span>Workiom'a Aktarıldı</span><span className="font-mono text-[10px]">{offer.workiomRecordId}</span></span>}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2 text-sm text-gray-700">
                                      <div><span className="font-medium">Sürücü:</span> {offer.supplierName}</div>
                                      <div><span className="font-medium">Firma:</span> {offer.carrierCompanyName || '-'}</div>
                                      <div><span className="font-medium">Telefon:</span> {offer.supplierContact || '-'}</div>
                                      <div><span className="font-medium">Çekici:</span> {offer.vehiclePlate || '-'}</div>
                                      <div><span className="font-medium">Dorse:</span> {offer.trailerPlate || '-'}</div>
                                      <div><span className="font-medium">Araç Türü:</span> {offer.vehicleType || '-'}</div>
                                      <div><span className="font-medium">Yüklemeye Uzaklık:</span> {offer.distanceToLoadingKm != null ? `${offer.distanceToLoadingKm} km` : '-'}</div>
                                    </div>
                                    {(offer.bidLatitude && offer.bidLongitude) && (
                                      <button
                                        type="button"
                                        onClick={() => openGoogleMapsCoordinates(offer.bidLatitude, offer.bidLongitude)}
                                        className="mb-2 text-left text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                      >
                                        Teklif konumu: {offer.bidLatitude.toFixed(5)}, {offer.bidLongitude.toFixed(5)}{offer.bidLocationTime ? ` • ${new Date(offer.bidLocationTime).toLocaleString('tr-TR')}` : ''}
                                      </button>
                                    )}
                                    {offer.notes && <div className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">{offer.notes}</div>}
                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                                      <span>Gönderilme: {new Date(offer.submittedAt).toLocaleString('tr-TR')}</span>
                                      <span>Geçerlilik: {new Date(offer.validUntil).toLocaleDateString('tr-TR')}</span>
                                    </div>
                                  </div>

                                  <div className="text-right shrink-0">
                                    <div className="text-2xl font-bold text-blue-600 mb-2">{offer.offerAmount.toLocaleString('tr-TR')} ₺</div>
                                    {offer.status === 'pending' && request.status !== 'completed' && request.status !== 'cancelled' && (
                                      <div className="flex flex-wrap gap-2 justify-end">
                                        <button onClick={() => openEditOfferModal(offer)} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">Düzenle</button>
                                        <button onClick={() => handleAcceptOffer(offer.id)} className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"><CheckCircle className="w-4 h-4" />Kabul Et</button>
                                        <button onClick={() => handleRejectOffer(offer.id)} className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"><XCircle className="w-4 h-4" />Reddet</button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            ))}

            {filteredRequests.length === 0 && (
              <div className="rounded-xl border border-gray-200 bg-white py-12 text-center text-gray-500">
                <FileText className="w-16 h-16 text-gray-300 mb-4 mx-auto" />
                Bu filtre için teklif talebi bulunamadı
              </div>
            )}
          </div>
        </div>
      )}

      {sharePreview.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !sharePreview.sending && setSharePreview({ open: false, channel: 'whatsapp', message: '', requestCount: 0, sending: false })}>
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="text-lg font-bold text-slate-900">{sharePreview.channel === 'whatsapp' ? 'WhatsApp' : 'Facebook'} Mesaj Önizleme</div>
              <div className="mt-1 text-sm text-slate-500">{sharePreview.requestCount} teklif talebi için gidecek mesajı kontrol edin.</div>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div className={`rounded-xl border p-3 text-sm ${sharePreview.channel === 'whatsapp' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
                {sharePreview.channel === 'whatsapp'
                  ? 'Mesaj, aktif WhatsApp paylaşım hedeflerine API üzerinden gönderilecektir.'
                  : 'Mesajı panoya kopyalayıp Facebook grup paylaşımında kullanabilirsiniz; kayıtlı Facebook hedefleri yeni sekmede açılacaktır.'}
              </div>
              <textarea
                value={sharePreview.message}
                onChange={(event) => setSharePreview((current) => ({ ...current, message: event.target.value }))}
                className="min-h-[360px] w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none focus:border-emerald-500"
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                disabled={sharePreview.sending}
                onClick={() => setSharePreview({ open: false, channel: 'whatsapp', message: '', requestCount: 0, sending: false })}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Vazgeç
              </button>
              <button
                type="button"
                disabled={sharePreview.sending}
                onClick={() => void (sharePreview.channel === 'whatsapp' ? openBulkWhatsappLocally() : openBulkFacebookLocally())}
                className={`inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-60 ${sharePreview.channel === 'whatsapp' ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
              >
                {sharePreview.channel === 'whatsapp' ? "WhatsApp'ta Aç" : "Facebook'ta Aç"}
              </button>
              {sharePreview.channel === 'whatsapp' ? (
                <button
                  type="button"
                  disabled={sharePreview.sending}
                  onClick={() => void submitBulkWhatsappShare()}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {sharePreview.sending ? 'Gönderiliyor...' : 'API ile Gönder'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {manualBidModal.open && manualBidModal.request && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Manuel Teklif Gir</h3>
              <p className="mt-1 text-sm text-gray-500">#{manualBidModal.request.sevkKodu} için dış sürücü seçerek teklif oluşturun.</p>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Dış Sürücü</label>
                <input
                  type="text"
                  value={manualBidDriverSearch}
                  onChange={(e) => setManualBidDriverSearch(e.target.value)}
                  className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Ad, telefon veya plaka ara"
                />

                <div className="max-h-72 overflow-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Sürücü</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Telefon</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Plaka</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Workiom ID</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Seç</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExternalDrivers.map((driver) => {
                        const selected = manualBidModal.driverId === driver.applicationId;
                        return (
                          <tr key={driver.id} className={selected ? 'bg-blue-50' : 'bg-white'}>
                            <td className="px-3 py-2 font-medium text-gray-900">{driver.name}</td>
                            <td className="px-3 py-2 text-gray-700">{driver.phone || '-'}</td>
                            <td className="px-3 py-2 text-gray-700">{driver.plate || '-'}</td>
                            <td className="px-3 py-2 text-xs font-mono text-gray-500">{driver.workiomRecordId || '-'}</td>
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => setManualBidModal((current) => ({ 
                                  ...current, 
                                  driverId: driver.applicationId,
                                  driverWorkiomId: driver.workiomRecordId || '',
                                  driverPlate: driver.plate || '',
                                  driverTrailerPlate: '',
                                  driverVehicleType: '',
                                }))}
                                className={`rounded-lg px-3 py-1.5 text-sm ${selected ? 'bg-blue-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                              >
                                {selected ? 'Seçildi' : 'Seç'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Teklif Tutarı</label>
                <input
                  type="number"
                  value={manualBidModal.amount}
                  onChange={(e) => setManualBidModal((current) => ({ ...current, amount: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Örn: 15000"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Not</label>
                <textarea
                  value={manualBidModal.notes}
                  onChange={(e) => setManualBidModal((current) => ({ ...current, notes: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  rows={3}
                  placeholder="Opsiyonel not"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setManualBidModal({ open: false, request: null, driverId: '', amount: '', notes: '', saving: false })}
                className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={submitManualBid}
                disabled={manualBidModal.saving}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
              >
                {manualBidModal.saving ? 'Kaydediliyor...' : 'Teklifi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editOfferModal.open && editOfferModal.offer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Teklifi Düzenle</h3>
              <p className="mt-1 text-sm text-gray-500">{editOfferModal.offer.supplierName} teklifini güncelleyin.</p>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Teklif Tutarı</label>
                <input
                  type="number"
                  value={editOfferModal.amount}
                  onChange={(e) => setEditOfferModal((current) => ({ ...current, amount: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Not</label>
                <textarea
                  value={editOfferModal.notes}
                  onChange={(e) => setEditOfferModal((current) => ({ ...current, notes: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button type="button" onClick={() => setEditOfferModal({ open: false, offer: null, amount: '', notes: '', saving: false })} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700">Vazgeç</button>
              <button type="button" onClick={submitOfferEdit} disabled={editOfferModal.saving} className="rounded-lg bg-blue-600 px-4 py-2 text-white disabled:opacity-50">
                {editOfferModal.saving ? 'Kaydediliyor...' : 'Güncelle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectOfferModal.open && rejectOfferModal.offer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Teklifi Reddet</h3>
              <p className="mt-1 text-sm text-gray-500">{rejectOfferModal.offer.supplierName} teklifinin neden reddedildiğini belirtin.</p>
            </div>
            <div className="px-6 py-5">
              <label className="mb-2 block text-sm font-medium text-gray-700">Ret Sebebi</label>
              <textarea
                value={rejectOfferModal.reason}
                onChange={(e) => setRejectOfferModal((current) => ({ ...current, reason: e.target.value }))}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                placeholder="Örn: Evrak eksik, fiyat uygun değil, araç tipi uygun değil..."
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button type="button" onClick={() => setRejectOfferModal({ open: false, offer: null, reason: '', saving: false })} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700">Vazgeç</button>
              <button type="button" onClick={submitRejectOffer} disabled={rejectOfferModal.saving} className="rounded-lg bg-red-600 px-4 py-2 text-white disabled:opacity-50">{rejectOfferModal.saving ? 'Kaydediliyor...' : 'Reddet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
